/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, August 8th 2022, 3:02:27 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import LinkIcon from "@mui/icons-material/Link";
import { isEmpty } from "lodash";
import { DatasetSchemaPicker } from "datasets/components/DatasetSchemaPicker";
import { useDatasetReferenceAPI } from "datasets/services/DatasetReferenceAPI";
import { DatasetReference, DatasetSchema } from "lib/schemas";
import { ToggleState, OSAlert, OSDropDown } from "ui-widgets";
import { OSPopUpAPIForm, TextFormInput, FormSubmitBubblingStopper } from "ui-widgets/forms";
import { OSAPIFormContext } from "ui-widgets/forms/providers";
import { DatasetReferenceRetreivalSettings } from "./DatasetReferenceRetreivalSettings";
import { DatasetReferenceUpdateSettings } from "./DatasetReferenceUpdateSettings";
import { NoDatasetFieldsCTA } from "./NoDatasetFieldsCTA";

export interface DatasetReferenceFormProps extends Pick<ToggleState, "open" | "handleClose"> {
  datasetReferenceId: DatasetReference["id"];
  settingsType?: "data_retrieval" | "data_update";
}

enum DatasetReferenceTypes {
  local = "Local",
  remote = "Remote",
}

export const DatasetReferenceForm = (props: DatasetReferenceFormProps) => {
  const {
    useAPIFormReturn,
    connectionNameLabel,
    dataSetReferenceType,
    isDatabaseTableReference,
    isRemoteSourceReference,
    hideDatasetSchemaSettings,
    ref_metadata,
    referencedDatasetSchema,
    datasetHasFields,
    onChangeDataset,
    setReferencedDatasetSchema,
    setDataSetReferenceType,
  } = useDatasetReferenceForm(props);

  const { handleClose, open, settingsType } = props;

  return (
    <FormSubmitBubblingStopper>
      <OSPopUpAPIForm<DatasetReference, DatasetReference>
        open={open}
        onClose={handleClose}
        useAPIFormReturn={useAPIFormReturn}
        popUpIcon={<LinkIcon color="primary" />}
        popUpTitle={<>Connect to a Dataset</>}
      >
        <OSAPIFormContext.Consumer>
          {({ useFormReturn, apiDataSendErrors }) => (
            <>
              {apiDataSendErrors.non_field_errors && (
                <OSAlert severity="error">
                  {apiDataSendErrors.non_field_errors.map((non_field_error) => (
                    <>
                      {non_field_error}
                      <br />
                    </>
                  ))}
                </OSAlert>
              )}

              <OSDropDown
                options={DatasetReferenceTypes}
                label="Dataset Reference Type"
                onChangeCallBack={setDataSetReferenceType}
                value={dataSetReferenceType}
              />

              {hideDatasetSchemaSettings ? null : (
                <DatasetSchemaPicker
                  datasetSchemaId={null}
                  datasetSchemaTag={ref_metadata?.json_dataset_schema}
                  handleChooseDatasetSchema={onChangeDataset}
                  onLoadInitial={(datasetSchema) => setReferencedDatasetSchema(datasetSchema)}
                />
              )}

              <TextFormInput<DatasetReference>
                schema_field_key="name"
                label={connectionNameLabel}
                disabled={isDatabaseTableReference}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              {hideDatasetSchemaSettings
                ? null
                : referencedDatasetSchema &&
                  (datasetHasFields ? (
                    <>
                      {settingsType === "data_update" ? (
                        <>
                          <DatasetReferenceUpdateSettings
                            referencedDatasetSchema={referencedDatasetSchema}
                            setReferencedDatasetSchema={setReferencedDatasetSchema}
                            useFormReturn={useFormReturn}
                          />
                        </>
                      ) : (
                        <>
                          <DatasetReferenceRetreivalSettings
                            referencedDatasetSchema={referencedDatasetSchema}
                            setReferencedDatasetSchema={setReferencedDatasetSchema}
                            useFormReturn={useFormReturn}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <NoDatasetFieldsCTA
                      datasetSchemaId={referencedDatasetSchema?.id}
                      datasetSchemaTitle={referencedDatasetSchema?.json_schema?.title}
                      dataBankId={referencedDatasetSchema?.data_bank}
                    />
                  ))}

              {isRemoteSourceReference && (
                <>
                  <TextFormInput
                    schema_field_key="ref_metadata.value_field"
                    label="Remote Value Field"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextFormInput
                    schema_field_key="ref_metadata.label_field"
                    label="Remote Label Field"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </>
              )}
            </>
          )}
        </OSAPIFormContext.Consumer>
      </OSPopUpAPIForm>
    </FormSubmitBubblingStopper>
  );
};

const useDatasetReferenceForm = ({ datasetReferenceId, settingsType }: DatasetReferenceFormProps) => {
  const { useDatasetReferenceAPIForm } = useDatasetReferenceAPI();
  const { useAPIFormReturn } = useDatasetReferenceAPIForm({ datasetReferenceId });

  const {
    useFormReturn: { setValue, watch },
  } = useAPIFormReturn;

  const [referencedDatasetSchema, setReferencedDatasetSchema] = React.useState<DatasetSchema>();

  const referencedDatasetFieldSchemas = referencedDatasetSchema?.json_schema?.properties;

  const datasetHasFields = !isEmpty(referencedDatasetFieldSchemas);

  const ref_metadata: DatasetReference["ref_metadata"] = watch("ref_metadata");

  const isDatabaseTableReference = Boolean(ref_metadata?.table_name);

  const connectionNameLabel = `Connection Name${
    isDatabaseTableReference ? " - This is a system created dataset and cannot be edited" : ""
  }`;

  const onChangeDataset = (datasetSchema: DatasetSchema) => {
    if (!datasetSchema) {
      setReferencedDatasetSchema(null);
      return;
    }

    setValue("ref_metadata", { ...ref_metadata, json_dataset_schema: datasetSchema.artifact_guid });
    setValue(
      "name",
      `${datasetSchema.json_schema?.title} Dataset ${settingsType === "data_update" ? "Update" : "Read"} Connection`
    );
    setReferencedDatasetSchema(datasetSchema);
  };

  const [dataSetReferenceType, setDataSetReferenceType] = React.useState<keyof typeof DatasetReferenceTypes>();

  React.useEffect(() => {
    if (
      !Boolean(ref_metadata?.table_name) &&
      !Boolean(ref_metadata?.json_dataset_schema) &&
      Boolean(ref_metadata.value_field)
    ) {
      setDataSetReferenceType("remote");
    } else {
      setDataSetReferenceType("local");
    }
  }, [ref_metadata?.table_name, ref_metadata?.json_dataset_schema, ref_metadata.value_field]);

  return {
    useAPIFormReturn,
    connectionNameLabel,
    isDatabaseTableReference,
    isRemoteSourceReference: dataSetReferenceType === "remote",
    dataSetReferenceType,
    ref_metadata,
    referencedDatasetSchema,
    datasetHasFields,
    hideDatasetSchemaSettings: dataSetReferenceType === "remote" || isDatabaseTableReference,
    onChangeDataset,
    setReferencedDatasetSchema,
    setDataSetReferenceType,
  };
};
